// General helper types that may be used in multiple areas
// can go here, helps organize vs too many inline ad-hoc types.
import * as ST from '@paytrace/pt-skel/type'
import { List } from 'lodash'

type Nullable<T> = T | null;
export interface MerchantListRecord extends ST.MerchantRecord { }
export {
  SearchStatus,
  type TransactionSearchResponse,
  type CardTransaction,
} from './Type/ViewTransaction'

export interface MerchantListRecord extends ST.MerchantRecord {}
export type PropsWithMetaData<P extends object> = P & { metadata?: object | undefined }

export interface UserRecord {
  is_admin?: boolean
  contact: string
}

export interface CtxUserRes {
  userRecord: UserRecord
  merchantList: MerchantListRecord[]
  fullMerchantList: MerchantListRecord[]
  featureFlags: { [key: string]: boolean }
}

export interface Customer {
  customer_label: string;
  name: string | null;
  customer_id: string;
  state: string;
}

export interface CustomerRecord {
  customer_id: number
  card_masked: string
  name: string
  email: string
  phone: string
  address: string
  address2: string
  country: string
  zip_code: string
  city: string
  state: string
  shipping_name: string
  shipping_address: string
  shipping_address2: string
  shipping_country: string
  shipping_zip_code: string
  shipping_city: string
  shipping_state: string
  exp_year: string
  exp_month: string
  customer_tax_id: string
  note: string
}

export interface CustomerDiscData {
  disc_id: number
  value: string
}

export interface DDPayload {
  disc_id: number
  disc_texts: { data_type: string, max: string, min: string }[]
  disc_values: { value: string }[]
  title: string
  type: string
  required: string
}

export interface ProcessorResponse {
  transaction_id: number
  response_code: number
  transaction_identifier: string
  response_message: string
  approval_code: string
  approval_message: string
}

export interface BatchData {
  detail: string
  response_code: number
  response_message: string
  transaction_identifier: number // same as transaction_id at top level
  approval_code: number
  approval_message: string
}

export interface BatchTransactionOpResult {
  transaction_id: string
  status_code: number
  data: BatchData | BatchData[]
}

export interface MerchantSurchargeInfo {
  surcharge_effective_rate?: number
  can_merchant_surcharge?: boolean
  is_surcharge_monthly_product?: boolean
  merchant_id?: number
  is_surcharge_enabled?: boolean
  is_merchant_agreed_to_terms?: boolean
  is_past_activation_date?: boolean
}

export interface MerchantTransactionEligibilityResponse {
  surcharge_effective_rate?: number
  can_merchant_surcharge?: boolean
  is_surcharge_monthly_product?: boolean
  is_card_holder_state_surchargeable?: boolean
  can_integrator_surcharge?: boolean
  is_merchant_surcharge_activated?: boolean
}

export interface BinInfoResponse {
  id?: number
  product_id?: string
  low_range?: number
  high_range?: number
  is_surchargeable?: boolean
  account_funding_source?: string
  account_funding_source_label?: string
}

export interface TransactionPreviewResponse {
  status: 'success' | 'error';
  data?: {
    card_info?: {
      expiration_month?: number | null;
      expiration_year?: number | null;
      card_holder_name?: string | null;
      is_expired?: boolean | null;
      masked?: string | null;
      first_part?: string | null;
      last_part?: string | null;
    };
    bin_preview_response?: {
      is_surchargeable?: boolean | null;
      level?: number | null;
      brand?: string | null;
      productid_or_clr?: string | null;
    },
    surcharge_preview_response?: {
      surcharge_effective_rate?: number | null;
      surcharge_amount?: number | null;
      surcharge_ineligibility_reason?: string | null;
      is_card_holder_state_surchargeable?: boolean | null;
    },
    swipe?: string | null;
  };
}

export interface StatusAndResponse<Response> {
  status: string
  data: Response
}

export interface TransactionSearchDD{
  disc_id: number
  disc_title: string
  type: string
  on_receipt: string
  required: string
  disc_values: (string | null)[]
}

export interface TransactionAddress{
  street?: string
  street2?: string
  city?: string
  state?: string
  postal_code?: string
  county?: string
  country?: string
}

export interface TransactionSearchRecord {
  status_changed_at?: Date
  transaction_id?: string
  card_first6?: string
  card_last4?: string
  card_expiration_month?: number
  card_expiration_year?: number
  requested_amount?: number
  created_at?: Date
  invoice_number?: string
  authorization_result_message?: string
  paytrace_user?: string
  merchant_id: number
  customer_id?: number
  description?: string
  shipping_name?: string
  billing_name?: string
  billing_email?: string
  billing_phone?: string
  surcharge_was_applied?: boolean
  action?: string
  card_brand?: string
  card_level?: number
  surcharge_amount?: number
  custom_dba?: string
  entry_method?: string
  card_masked?: string
  processor?: string
  status?: string
  settle_amount?: number
  auth_method?: string
  disc_data?: TransactionSearchDD[]
  approval_code?: string
  processing_action?: string
  tax_amount?: number
  customer_reference?: string
  billing_address?: TransactionAddress
  shipping_address?: TransactionAddress
}

export interface PermissionCheckProps {
  records: {user : ST.UserRecord, company : ST.CompanyRecord},
  required_permissions: string[];
  disallowed_permissions?: string[]
  do_admin_bypass?: boolean;
}

export type FieldMap<S extends object, T extends object> = Record<keyof S, keyof T>
export type SelfKVPair<T> = Record<keyof T, T>

export interface MerchantEnhancedDataProduct {
  priority: number | null
  description: string | null
  product_code: string
  commodity_code: string | null
  unit_of_measure: string | null
  unit_cost: number | null
  product_label: string
  merchant_id: number
  product_id: number
  created_by: string | null
  created_at: string
}

export interface MerchantProductsResponse {
  status: string
  status_code: number
  data: MerchantEnhancedDataProduct[]
}

export interface BaseCustomerListReq {
  name_filter?: string;
  filter_type?: string;
  limit?: number;
  offset?: number;
  include_card_number?: boolean;
  last_activity_days?: number;
  include_associated_merchants?: boolean;
  includeCustomerId?: number;
}

export interface CustomerListReq extends BaseCustomerListReq {
  merchantId: string;
}
export const RECUR_TRANSACTION_TYPE = {
  SALE: 'Sale',
  AUTHORIZATION: 'Authorization',
}
export const RecurTransactionTypeMap = [
  { val: RECUR_TRANSACTION_TYPE.SALE, name: RECUR_TRANSACTION_TYPE.SALE },
  {
    val: RECUR_TRANSACTION_TYPE.AUTHORIZATION,
    name: RECUR_TRANSACTION_TYPE.AUTHORIZATION,
  },
]

export const RECUR_FREQUENCY = {
  ANNUALLY: 'Annually',
  SEMI_ANNUALLY: 'Semi Annually',
  TRIMESTERLY: 'Trimesterly',
  QUARTERLY: 'Quarterly',
  BI_MONTHLY: 'Bi-monthly',
  MONTHLY: 'Monthly',
  WEEKLY: 'Weekly',
  BI_WEEKLY: 'Bi-weekly',
  FIRST_AND_15TH: '1st and 15th',
  DAILY: 'Daily',
}

export const RECUR_FREQUENCY_VALUE = {
  ANNUALLY: '1',
  SEMI_ANNUALLY: '8',
  TRIMESTERLY: 'A',
  QUARTERLY: '2',
  BI_MONTHLY: '9',
  MONTHLY: '3',
  WEEKLY: '5',
  BI_WEEKLY: '4',
  FIRST_AND_15TH: '7',
  DAILY: '6',
} as const

export const RecurFrequencyMap = [
  { val: RECUR_FREQUENCY_VALUE.ANNUALLY, name: RECUR_FREQUENCY.ANNUALLY },
  { val: RECUR_FREQUENCY_VALUE.SEMI_ANNUALLY, name: RECUR_FREQUENCY.SEMI_ANNUALLY },
  { val: RECUR_FREQUENCY_VALUE.TRIMESTERLY, name: RECUR_FREQUENCY.TRIMESTERLY },
  { val: RECUR_FREQUENCY_VALUE.QUARTERLY, name: RECUR_FREQUENCY.QUARTERLY },
  { val: RECUR_FREQUENCY_VALUE.BI_MONTHLY, name: RECUR_FREQUENCY.BI_MONTHLY },
  { val: RECUR_FREQUENCY_VALUE.MONTHLY, name: RECUR_FREQUENCY.MONTHLY },
  { val: RECUR_FREQUENCY_VALUE.WEEKLY, name: RECUR_FREQUENCY.WEEKLY },
  { val: RECUR_FREQUENCY_VALUE.BI_WEEKLY, name: RECUR_FREQUENCY.BI_WEEKLY },
  { val: RECUR_FREQUENCY_VALUE.FIRST_AND_15TH, name: RECUR_FREQUENCY.FIRST_AND_15TH },
  { val: RECUR_FREQUENCY_VALUE.DAILY, name: RECUR_FREQUENCY.DAILY },
]

export const RECUR_PROCESS_ON_WEEKENDS = {
  YES: 'Yes, process according to schedule',
  NO: 'No, process on next weekday',
}

export const RecurProcessOnWeekendsMap = [
  {
    val: true,
    name: RECUR_PROCESS_ON_WEEKENDS.YES,
  },
  {
    val: false,
    name: RECUR_PROCESS_ON_WEEKENDS.NO,
  },
]
export const RECUR_PAYMENT_TYPE = {
  CREDIT_CARD: 'Credit Card',
  BANK_ACCOUNT: 'Bank Account',
}
export const RecurPaymentTypes = [
  { val: 'C', name: RECUR_PAYMENT_TYPE.CREDIT_CARD },
  {
    val: 'A',
    name: RECUR_PAYMENT_TYPE.BANK_ACCOUNT,
  },
]

export const RECUR_PAYMENT_VALUE = {
  CREDIT_CARD: 'C',
  BANK_ACCOUNT: 'A',
} as const

export interface RecurFormData {
  transaction_type?: string;
  amount?: number;
  surcharge_amount?:number;
  total_amount?:number;
  frequency?: string;
  process_on_weekends?: boolean;
  start_date?: Date;
  next_date?: Date;
  no_of_payment_processed?: string
  transaction_count?: string;
  no_of_attempts_if_declined?: number;
  payment_type?: string;
  description?: string;
  customer_receipt: boolean;
  sec_code?: string;
  id?: number;
  date_created?: string;
  username?: string;
  ip_address?: string;
  merchant_id?: string;
  customer_id?: number;
  add_surcharge?: boolean;
  confirm_surcharge_enable?:boolean,
  customer_custid?: string,
  customer_billing_name?: string,
  when: string
  merchant_local_time: string,
}

export interface RecurChangeLogData{
  customer_link?: string,
  customer_name?: string | null,
  apply_surcharge?: string | undefined,
  add_surcharge?: boolean,
  amount: number | undefined,
  current_count: number,
  customer_id: number | undefined,
  send_customer_recurring_receipt: string,
  frequency: string,
  merchant_id: number | undefined,
  ip_address: string| undefined,
  next_processing_date: Date | undefined,
  number_of_retries_if_declined: number | undefined,
  recurring_id: number | undefined,
  recurring_change_log_id: number | null,
  sec_code: string | undefined,
  user_that_made_last_change: string | undefined,
  total_count: number,
  transaction_type: string| undefined,
  process_on_weekend: string,
  date_changed: string | undefined
}

export const CHANGE_LOG_WEEKENDS = {
  YES: 'True',
  NO: 'False',
}
export const ChangeLogWeekendsMap = [
  { val: 'Y', name: CHANGE_LOG_WEEKENDS.YES },
  { val: '', name: CHANGE_LOG_WEEKENDS.NO },
  { val: 'N', name: CHANGE_LOG_WEEKENDS.NO },
]

export const CHANGE_LOG_RECEIPT = {
  YES: 'True',
  NO: 'False',
}
export const ChangeLogReceiptMap = [
  { val: 'Y', name: CHANGE_LOG_RECEIPT.YES },
  { val: '', name: CHANGE_LOG_RECEIPT.NO },
  { val: 'N', name: CHANGE_LOG_RECEIPT.NO },
]

export const CHANGE_LOG_SURCHARGE = {
  DISABLED: 'Disabled',
  ENABLED: 'Enabled',
}
export const ChangeLogSurchargeMap = [
  { val: false, name: CHANGE_LOG_SURCHARGE.DISABLED },
  { val: true, name: CHANGE_LOG_SURCHARGE.ENABLED },
]

export const RecurAttemptMap = [
  { val: '0', name: '0' },
  { val: '1', name: '1' },
  { val: '2', name: '2' },
  { val: '3', name: '3' },
  { val: '4', name: '4' },
  { val: '5', name: '5' },
  { val: '6', name: '6' },
]

export interface RecurRequest {
  merchant_id?: number,
  customer_id?: number,
  recurrence: {
    amount?: number,
    customer_receipt?: boolean,
    frequency?: string,
    start_date?: string,
    total_count?: string,
    transaction_type?: string,
    type?: string,
    description?: string,
    sec_code?: string,
    id?: number,
    repeat?: number,
    weekend?: boolean
  },
  next_date?: string,
  surcharge_info: {
    omit_surcharge: boolean
  }
}

export const stdEntryClasses = [
  { name: '', val: '' },
  { name: 'CCD', val: 'CCD' },
  { name: 'PPD', val: 'PPD' },
  { name: 'WEB', val: 'WEB' },
  { name: 'TEL', val: 'TEL' },
]

export interface ACHMerchantSettings {
  merchant_id: number,
  processor_id: number,
  processor_code: string,
  processor_status: number, // boolean?
  std_entry_classes: List<any>,
  bank_account_hrid?: string
}

export type ChangeLogHistoryResponse = {
  add_surcharge: boolean,
  amount: number,
  current_count: number,
  customer_custid: number,
  customer_billing_name: string,
  customer_id: string,
  customer_receipt: string,
  frequency: string,
  merchant_id: number,
  ip_address: string,
  next_processing_date: Date,
  repeat: number,
  recurring_id: number,
  recurring_log_id: number,
  sec_code: string | undefined,
  username: string,
  total_count: number,
  transaction_type: string,
  process_on_weekend: string,
  when: string,
}

export interface Recur {
  recurring_id: number,
  merchant_id: string,
  frequency: string,
  amount: number,
  customer_id: string,
  transaction_type: string,
  customer_receipt: string,
  total_count: string,
  current_count: string,
  next_processing_date: Date,
  start_date: string,
  process_on_weekend: string,
  username: string,
  repeat: number,
  sec_code: string,
  add_surcharge: boolean,
  description: string,
  recurring_type: string,
  customer_custid: string,
  customer_billing_name: string,
  ip_address: string,
  when: string,
  merchant_local_time: string,
}

export type CustomerAchDetails = {
  customer_id: number | null
  merchant_id: number | null
  customer_label: string | null
  name: string | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  card_masked: string | null
  address: string | null
  address2: string | null
  country: string | null
  zip_code: string | null
  city: string | null
  state: string | null
  shipping_name: string | null
  shipping_address: string | null
  shipping_address2: string | null
  shipping_county: string | null
  shipping_country: string | null
  shipping_zip_code: string | null
  shipping_city: string | null
  shipping_state: string | null
  exp_month: string | null
  exp_year: string | null
  cid: number | null
  custid: string | null
  id: number | null
  customer_tax_id: null
  note: string | null
  account_number: string | null
  routing_number: string | null
  account_type: string | null
  std_entry_class: string | null
}

export type AchDetails = Pick<CustomerAchDetails, 'routing_number' | 'account_number' | 'std_entry_class'>

interface CardInfo {
    card_holder_name: Nullable<string>;
    expiration_month: Nullable<number>;
    expiration_year: Nullable<number>;
    first_part: Nullable<string>;
    is_expired: Nullable<boolean>;
    last_part: Nullable<string>;
    masked: Nullable<string>;
}

interface BinPreviewResponse {
    account_funding_source: Nullable<string>;
    account_funding_source_label: Nullable<string>;
    brand: Nullable<string>;
    high_range: Nullable<number>;
    id: Nullable<number>;
    is_surchargeable: Nullable<boolean>;
    level: Nullable<number>;
    low_range: Nullable<number>;
    processor: Nullable<string>;
    processor_response_code: Nullable<string>;
    product_id: Nullable<string>;
    productid_or_clr: Nullable<string>;
    purchasing_card_indicator: Nullable<string>;
    settlement_amount: Nullable<number>;
}

interface SurchargePreviewResponse {
    can_integrator_surcharge: Nullable<boolean>;
    can_merchant_surcharge: Nullable<boolean>;
    is_merchant_surcharge_activated: Nullable<boolean>;
    manual_surcharge_amount: Nullable<number>;
    omit_surcharge: Nullable<boolean>;
    pre_surcharge_amount: Nullable<number>;
    surcharge_amount: Nullable<number>;
    surcharge_api_response_code: Nullable<string>;
    surcharge_effective_rate: Nullable<number>;
    surcharge_ineligibility_reason: Nullable<string>;
}

export interface PaymentPreviewData {
    bin_preview_response: Nullable<BinPreviewResponse>;
    card_info: Nullable<CardInfo>;
    surcharge_preview_response: Nullable<SurchargePreviewResponse>;
}
